import { useTranslation } from './I18nProvider';

/** Below this, "just now": a 10-minute duty cycle makes sub-minute precision noise. */
const JUST_NOW_MS = 45_000;

/**
 * How long ago a fix arrived, in the active language.
 *
 * <p>Replaces the English-only `formatAge`, which produced sentences like "zadnja
 * lokacija just now" — half-translated, and on the map pins that half is inside the only
 * thing a screen reader gets. A language switch that leaves the most repeated string on
 * the screen in English is not a language switch.
 *
 * <p>Returns `null` when there has never been a fix, because "no fix yet" is a different
 * statement from "0 minutes ago" and the caller words it differently.
 */
export function useAge(): (ageMs: number | null) => string | null {
  const { t, fmt } = useTranslation();

  return (ageMs) => {
    if (ageMs === null) return null;

    // A future timestamp is not an age. Clamping it to zero would tell a reader that an
    // invitation expiring in a week had already lapsed.
    const future = ageMs < 0;
    const absolute = Math.abs(ageMs);
    if (absolute < JUST_NOW_MS) return t.age.justNow;

    const minutes = Math.round(absolute / 60_000);
    if (minutes < 60) {
      return future ? t.age.inMinutes(fmt.number(minutes)) : t.age.minutes(fmt.number(minutes));
    }

    const hours = Math.floor(minutes / 60);
    if (hours < 24) {
      const remainder = minutes % 60;
      return future
        ? t.age.inHours(fmt.number(hours), remainder)
        : t.age.hours(fmt.number(hours), remainder);
    }

    const days = Math.floor(hours / 24);
    if (days === 1) return future ? t.age.tomorrow : t.age.yesterday;
    return future ? t.age.inDays(days) : t.age.days(days);
  };
}
